// Auth access layer. Identity lives in the Redux auth slice
// (`store/slices/auth.slice`); this file exposes the same ergonomics the app
// already uses:
//   - <AuthProvider> — a thin wrapper that rehydrates a persisted session once on
//     mount (dispatches bootstrapAuth). It renders children; no React Context.
//   - useAuth() — a hook returning identity state + login/logout/selectAccount,
//     all backed by the slice. Consumers (RequireAuth, Login, Landing, Dashboard,
//     AppLayout) are unchanged.
import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { STORAGE_KEYS } from '../config/constants';
import {
  bootstrapAuth, loginUser, fetchUsers, setSelectedAccount, clearAuth, selectAuth,
} from '../store/slices/auth.slice';
import { OIDC_ENABLED } from './oidc/oidcConfig';
import { signoutRedirect } from './oidc/userManager';
import { EMBEDDED_ENABLED } from './embedded/embeddedConfig';
import { clearEmbeddedToken } from './embedded/embeddedToken';
import { clearLegacySessionParameter } from './embedded/legacySession';
import { clearParentCapabilities } from './embedded/parentNavigation';

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  // Re-establish a persisted session on app start (no-op when none — guarded by
  // the thunk's `condition`).
  useEffect(() => {
    // In OIDC mode the /auth/callback route owns the login handshake (AuthCallback
    // completes the code exchange, then calls /me with the fresh token). bootstrapAuth
    // must NOT also run there: on a user switch the *previous* user's id can still be
    // in localStorage when the callback page loads, so bootstrap would fire /me with a
    // stale/expired token and 401. Skip it on the callback; AuthCallback drives auth.
    if (OIDC_ENABLED && window.location.pathname.startsWith('/auth/callback')) return;
    dispatch(bootstrapAuth());
  }, [dispatch]);
  return children;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

/** Identity + actions, backed by the Redux auth slice. */
export function useAuth() {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);

  // Persist identity before /me so the client interceptor attaches X-User-Id;
  // .unwrap() surfaces rejections to the caller (Login awaits/catches this).
  const login = useCallback((id) => {
    localStorage.setItem(STORAGE_KEYS.USER_ID, id);
    return dispatch(loginUser(id)).unwrap();
  }, [dispatch]);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID);
    localStorage.removeItem(STORAGE_KEYS.SPONSOR_PLAN_ID);
    dispatch(clearAuth());
    // RP-initiated logout ends the IdP SSO session (a local-only clear can't — the
    // session cookie lives on the IdP domain), so a different user can sign in next.
    // This does a full-page redirect to the IdP and back to /login.
    if (OIDC_ENABLED) signoutRedirect();
    // Embedded mode: drop the in-memory/sessionStorage session token so a return to
    // /login re-runs the parent handshake (the parent's .NET session stays authoritative).
    // Everything the parent handed us in the handshake goes at once — the session
    // token, the legacy RANDOMNUM and the parent's declared capabilities — so a return
    // to /login re-asks the parent for all three.
    if (EMBEDDED_ENABLED) {
      clearEmbeddedToken();
      clearLegacySessionParameter();
      clearParentCapabilities();
    }
  }, [dispatch]);

  const selectAccount = useCallback((accountId) => {
    if (accountId) localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId);
    dispatch(setSelectedAccount(accountId));
  }, [dispatch]);

  const loadUsers = useCallback(() => dispatch(fetchUsers()), [dispatch]);

  return useMemo(() => ({
    userId: auth.userId,
    participant: auth.participant,
    accounts: auth.accounts,
    selectedAccountId: auth.selectedAccountId,
    // Relius context handed over by the embedding parent ({ sessionId, userId, planId });
    // null outside embedded mode. Read-only — Relius calls themselves stay backend-side.
    session: auth.session,
    status: auth.status,
    error: auth.error,
    isAuthenticated: auth.status === 'authenticated',
    // Persona (from /me). `role` drives the two authorization gates — <RequireRole>
    // for routes and <Can> inline — and nothing else in the app branches on it.
    role: auth.role,
    isSponsor: auth.role === 'sponsor',
    sponsorPlans: auth.sponsorPlans,
    users: auth.users,
    usersStatus: auth.usersStatus,
    usersError: auth.usersError,
    login,
    logout,
    selectAccount,
    loadUsers,
  }), [auth, login, logout, selectAccount, loadUsers]);
}

export default AuthProvider;
